import { isValidPhoneNumber } from "react-phone-number-input";
import * as z from "zod";

import {
  FRIENDLY_NAME_MAX_LENGTH,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  USER_ID_MAX_LENGTH,
  USER_ID_MIN_LENGTH,
  hasPasswordCriteria,
  hasSpaces,
} from "./field-validations";

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, {
    error: `Password must be at least ${PASSWORD_MIN_LENGTH} characters or more.`,
  })
  .max(PASSWORD_MAX_LENGTH, {
    error: formatZodMaxError("Password"),
  })
  .refine((val) => !hasSpaces(val), {
    error: "Password may not contain spaces.",
  })
  .refine((val) => hasPasswordCriteria(val), {
    error: "Password does not meet criteria.",
  });

export const phoneSchema = z
  .string()
  .min(1, {
    error: "Phone number is required.",
  })
  .refine((val) => isValidPhoneNumber(val), {
    error: "Phone number is invalid.",
  });

export const ssnSchema = z
  .string()
  .min(1, {
    error: "Social security number is required.",
  })
  .regex(/^\d{3}-\d{2}-\d{4}$/, {
    error: "SSN must be in the format xxx-xx-xxxx",
  });

export const emailSchema = z
  .string()
  .min(1, {
    error: "Email is required.",
  })
  .pipe(
    z.email({
      error: "Email is invalid.",
    }),
  );

export const friendlyNameSchema = z.string().max(FRIENDLY_NAME_MAX_LENGTH, {
  error: formatZodMaxError("Friendly Name"),
});

export const zipCodeSchema = z
  .string()
  .min(1, {
    error: "ZIP code is required.",
  })
  .regex(/^([0-9]{5})$/, {
    error: "ZIP code must be 5 digits only.",
  });

export const userIdSchema = z
  .string()
  .min(USER_ID_MIN_LENGTH, {
    error: `User ID must be at least ${USER_ID_MIN_LENGTH} characters long.`,
  })
  .max(USER_ID_MAX_LENGTH, {
    error: formatZodMaxError("User ID"),
  })
  .refine((val) => !hasSpaces(val), {
    error: "User ID may not contain spaces.",
  });

/**
 * Formats a Zod max error message.
 * @param fieldName - The name of the field.
 * @returns A function that takes a Zod issue and returns a formatted error message.
 */
function formatZodMaxError<
  TIssue extends Parameters<
    z.core.$ZodErrorMap<z.core.$ZodIssueTooBig<z.core.util.HasLength>>
  >[0],
>(fieldName: string): (issue: TIssue) => string {
  return (issue: TIssue) =>
    [
      `${fieldName} must be ${issue.maximum} characters or less.`,
      `Remove ${issue.input?.length ? issue.input.length - (issue.maximum as number) : 0} characters.`,
    ].join(" ");
}
